import { isNull } from "lodash";
import { EditableCell } from "./EditableCell";
import { EditableSelectCell } from "./EditableSelectCell";
import { Cell, flexRender, ColumnDef } from "@tanstack/react-table";

const REDUNDANT_MOBILE_COLUMN_LABELS = new Set(["Name", "Title"]);

export function shouldHideMobileColumnLabel(columnDef: {
  header?: unknown;
  meta?: { headerClassName?: string };
}): boolean {
  if (columnDef.meta?.headerClassName?.includes("hide-header-in-column")) {
    return true;
  }

  return typeof columnDef.header === "string" && REDUNDANT_MOBILE_COLUMN_LABELS.has(columnDef.header);
}

export function renderEmptyCellState(value: any): React.ReactNode {
  // Check for null, undefined, and empty string specifically (not just !value)
  // This preserves meaningful falsy values like 0, false, etc. that should display in tables
  if (value === null || value === undefined || value === "") {
    return <span className="opacity-20">--</span>;
  }
  return value;
}

// Apply empty state detection to all columns in an array
export function withGlobalEmptyStates<T>(columns: ColumnDef<T, any>[]): ColumnDef<T, any>[] {
  return columns.map((column) => {
    // Skip if column explicitly opts out
    if (column.meta?.skipEmptyState) {
      return column;
    }

    // Skip if no way to get data (no accessorKey and no cell function)
    if (!("accessorKey" in column) && !column.cell) {
      return column;
    }

    // Clone the column to avoid mutation
    const enhancedColumn = { ...column };

    if (enhancedColumn.cell && typeof enhancedColumn.cell === "function") {
      // Wrap existing cell function with empty state detection
      const originalCell = enhancedColumn.cell;
      enhancedColumn.cell = (props: any) => {
        const result = originalCell(props);
        return renderEmptyCellState(result);
      };
    } else if ("accessorKey" in enhancedColumn) {
      // Create cell function for simple accessor columns
      enhancedColumn.cell = (props: any) => {
        const value = props.getValue();
        return renderEmptyCellState(value);
      };
    }

    return enhancedColumn;
  });
}
export function chunkBy<T, K>(arr: T[], getKey: (a: T) => K): T[][] {
  const pushHead = ([head = [], ...tail]: T[][], curr: T) => [[curr, ...head], ...tail];

  return arr
    .reduce<{ chunks: T[][]; key?: K }>(
      ({ chunks: acc, key: prevKey }, curr) => {
        const key = getKey(curr);
        return { chunks: key === prevKey ? pushHead(acc, curr) : [[curr], ...acc], key };
      },
      { chunks: [] }
    )
    .chunks.map((chunk) => chunk.reverse())
    .reverse();
}

export function renderColumnValue<T>(cell: Cell<T, unknown>, isCompactLayout?: boolean) {
  const { column, getValue } = cell;
  const cellValue = getValue();

  // Only apply empty state detection to simple cases, unless column opts out
  if (isNull(cellValue) && !column.columnDef.meta?.skipEmptyState) {
    return <div className="opacity-20">--</div>;
  }

  const meta = cell.getContext().table.options.meta;
  const editable = column.columnDef.meta?.editable;
  const isEditable = typeof editable === "function" ? editable(cell.row) : editable;
  const icon = column.columnDef.meta?.editableIcon?.(cell);

  if (isEditable && !isCompactLayout) {
    const editCallbacks = {
      onStartEdit: () => {
        meta?.setEditableRowIndex?.(cell.row.index);
        meta?.setEditableCellId?.(cell.id);
      },
      onEndEdit: () => {
        meta?.setEditableCellId?.(null);
        meta?.setEditableRowIndex?.(null);
      }
    };

    if (column.columnDef.meta?.editableInputType === "select") {
      return (
        <EditableSelectCell
          value={String(cellValue)}
          options={column.columnDef.meta?.editableSelectOptions ?? []}
          onChange={(value) => column.columnDef.meta?.onEdit?.(value, cell)}
          className={column.columnDef.meta?.editableClassName}
          isEditingCell={meta?.editableCellId === cell.id}
          {...editCallbacks}
        />
      );
    }

    return (
      <EditableCell
        value={String(cellValue)}
        onChange={(value) => column.columnDef.meta?.onEdit?.(value, cell)}
        className={column.columnDef.meta?.editableClassName}
        icon={icon}
        isEditingCell={meta?.editableCellId === cell.id}
        {...editCallbacks}
        editableInputType={column.columnDef.meta?.editableInputType ?? "default"}
      />
    );
  }

  const cellFunctionResult =
    typeof column.columnDef.cell === "function" ? column.columnDef.cell(cell.getContext()) : undefined;

  const renderedCell = flexRender(column.columnDef.cell, cell.getContext());

  const emptyValues: unknown[] = [null, undefined, false];
  const isEmpty = emptyValues.includes(cellFunctionResult) || emptyValues.includes(renderedCell);

  const shouldSkipEmptyState =
    isEmpty &&
    isCompactLayout === true &&
    column.columnDef.meta?.skipEmptyState &&
    column.columnDef.meta?.headerClassName?.includes("hide-header-in-column");

  if (shouldSkipEmptyState) {
    return null;
  }

  if (isEmpty) {
    return <div className="opacity-20">--</div>;
  }

  // If there's an icon but the field is not editable, display the icon alongside the value
  if (icon && !isEditable && !isCompactLayout) {
    return (
      <div className="flex flex-row gap-1 items-center relative">
        <div className="absolute left-1">{icon}</div>
        <div className="pl-8">{renderedCell}</div>
      </div>
    );
  }

  return renderedCell;
}

export function compareNullableIsoDates(
  a: string | null | undefined,
  b: string | null | undefined,
  isDesc: boolean
): number {
  if (!a && !b) {
    return 0;
  }

  if (!a) {
    return isDesc ? -1 : 1;
  }

  if (!b) {
    return isDesc ? 1 : -1;
  }

  return a < b ? -1 : a > b ? 1 : 0;
}

export function customAlphanumericSorting(rowA: any, rowB: any, columnId: any) {
  const a = rowA.getValue(columnId);
  const b = rowB.getValue(columnId);

  if (a == null && b == null) return 0;
  if (a == null) return -1; // null values go to the start
  if (b == null) return 1; // null values go to the start

  const aStr = String(a).toUpperCase();
  const bStr = String(b).toUpperCase();

  const aIsNum = !isNaN(Number(aStr));
  const bIsNum = !isNaN(Number(bStr));

  if (aIsNum && bIsNum) {
    return Number(aStr) - Number(bStr);
  }

  if (aIsNum) return -1;
  if (bIsNum) return 1;

  return aStr.localeCompare(bStr);
}
